import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Link, useLocation, useParams } from 'react-router-dom';
import Details, { mapByRefValue } from '../../Details/Details/Details';
import useAuth from '../../Services/useAuth';
import { useGetRequestById } from '../../Services/useGetRequestById';
import { useGetMyRequest } from '../../Services/useGetMyRequest';
import Loader from '../../UIComponents/Loader';
import { communicationData, personalData } from '../../helperFuncs';
import RegistrationSuccess from './RegistrationSuccess';
import { useEffect, useState } from 'react';
import ProtectedRouteViolation from '../AuthPages/ProtectedRouteViolation';
import { downloadFile } from '../../Services/httpCalls';
import GovButton from '../../UIComponents/Buttons/GovButton';
import { enqueueSnackbar } from 'notistack';

const InfoRow = ({ label, children, sx = {}, show = true }) => {
  if (!show) return null;
  return (
    <Box>
      <Grid
        container
        display="flex"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={sx}
        className="grid-success-view"
      >
        <Grid item xs={6}>
          <Typography variant="h3">{label}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{children}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const RegistrationPreview = () => {
  const { id } = useParams();
  const [isLinkVisible, setIsLinkVisible] = useState(false);
  const { getRole, getUser } = useAuth();
  const location = useLocation();
  const isAdmin = getRole() === 'admin';
  const adminRequestQuery = useGetRequestById(id, isAdmin);
  // Users fetch only their own request; the URL id is used just as a cache key.
  const myRequestQuery = useGetMyRequest({
    userId: getUser()?.sub,
    scope: id,
    enabled: !isAdmin,
  });
  const { data: request, isLoading: isRequestLoading } = isAdmin
    ? adminRequestQuery
    : myRequestQuery;
  const currentRequest = request?.data?.requests?.[0];
  const mappedPersonalData =
    currentRequest &&
    mapByRefValue(
      {
        ...currentRequest.user,
        amka: currentRequest.amka,
      },
      personalData,
    );
  const mappedCommunicationData =
    currentRequest &&
    mapByRefValue(
      {
        ...currentRequest,
        email: currentRequest.user?.email,
      },
      communicationData,
    );
  useEffect(() => {
    // Delay the appearance of the Link by 4 seconds
    const timer = setTimeout(() => {
      setIsLinkVisible(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const fileDownload = async (request_id, profession_id, file_name) => {
    try {
      await downloadFile(request_id, profession_id, file_name);
    } catch (err) {
      enqueueSnackbar('Παρουσιάστηκε σφάλμα στο κατέβασμα του αρχείου', {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const municipalitiesView = currentRequest?.municipalities
    ? currentRequest.municipalities.map((item) => item.municipality).join(',') +
      ', '
    : '';

  const regionalUnitsView = currentRequest?.regional_units
    ? currentRequest.regional_units
        .map((item) => item.regional_unit)
        .join(',') + ', '
    : '';

  const regionsUnitsView = currentRequest?.regions
    ? currentRequest.regions.map((item) => item.region).join(',') + ', '
    : '';

  const mappedProfesionalData = currentRequest && [
    {
      name: 'Χωροταξική δραστηριοποίηση',
      refValue: 'spatialActivity',
      id: 0,
      value: `${regionsUnitsView}${regionalUnitsView}${municipalitiesView}`,
    },
    ...(currentRequest.professions ?? []).flatMap((item, index) => [
      {
        name: 'Ειδικότητα/ Επάγγελμα',
        refValue: 'job',
        id: index + 1,
        value: item.profession,
      },
      {
        name: 'Τεκμηρίωση επαγγέλματος/ειδικότητας',
        refValue: 'proof_description',
        id: index + 2,
        value: item.proof_description,
      },

      {
        name: 'Τίτλος σπουδών',
        refValue: 'profession_proof',
        id: index + 3,
        value:
          item.proof === 4 ? (
            isLinkVisible ? (
              <Typography
                component="a"
                sx={{
                  cursor: 'pointer',
                  color: 'primary.main',
                  fontWeight: 'bold',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={() =>
                  fileDownload(
                    currentRequest?.id,
                    item?.profession_id,
                    item?.file?.name,
                  )
                }
              >
                {item?.file?.name}
              </Typography>
            ) : (
              <Box sx={{ height: '50px' }}>
                <Loader />
              </Box>
            )
          ) : (
            'Δεν απαιτείται'
          ),
      },
    ]),
  ];

  const communicationGroupData = currentRequest?.communication_groups?.map(
    (item) => item.category,
  );

  const UserCertificationSection = () => (
    <InfoRow label="Πιστοποιημένος χρήστης">
      {currentRequest?.user?.is_certified === 1 ? (
        'Ναι'
      ) : (
        <Link
          style={{ textDecoration: 'none', color: 'blue' }}
          to="/registration-form"
        >
          Όχι
        </Link>
      )}
    </InfoRow>
  );

  const UserStatusSection = () => (
    <InfoRow label="Κατάσταση" sx={{ my: 3 }} show={getRole() === 'admin'}>
      {currentRequest?.user?.is_active === 1 ? 'Ενεργός' : 'Ανενεργός'}
    </InfoRow>
  );

  const ProtocolSection = () => (
    <InfoRow label="Αριθμός πρωτοκόλλου" sx={{ my: 3 }}>
      {currentRequest?.protocol ?? ''}
    </InfoRow>
  );

  return (
    <>
      {currentRequest &&
      currentRequest.user?.id !== getUser()?.sub &&
      getRole() === 'user' &&
      !isRequestLoading ? (
        <ProtectedRouteViolation />
      ) : isRequestLoading ? (
        <Loader />
      ) : (
        <Box>
          <Box
            className={
              location.pathname.includes('success')
                ? 'registration-success'
                : ''
            }
          >
            {location.pathname.includes('success') && <RegistrationSuccess />}
          </Box>

          {getRole() !== 'admin' ? (
            <Typography sx={{ my: 3 }} className="typography-subtitle-success">
              Εγγραφή στο μητρώο απασχολούμενων του πολιτιστικού και
              δημιουργικού τομέα
            </Typography>
          ) : null}

          <Typography
            variant="h1"
            sx={{ my: 3 }}
            className="typography-subtitle-success"
          >
            Προβολή
          </Typography>
          {isRequestLoading ? (
            <Loader />
          ) : (
            <Box>
              <ProtocolSection />
              <UserCertificationSection />
              <UserStatusSection />

              <Typography variant="h3" sx={{ my: 3 }}>
                Προσωπικά στοιχεία
              </Typography>
              <Details id={'personalData'} rows={mappedPersonalData} />
              <Typography variant="h3" sx={{ my: 3 }}>
                Στοιχεία επικοινωνίας
              </Typography>
              <Details rows={mappedCommunicationData} />
              <Typography variant="h3" sx={{ my: 3 }}>
                Στοιχεία Δραστηριοποίησης
              </Typography>
              <Details rows={mappedProfesionalData} />
              <Box sx={{ mb: 15 }}>
                <Typography variant="h3" sx={{ my: 3 }}>
                  Επικοινωνία
                </Typography>
                <Details
                  rows={[
                    {
                      id: 0,
                      name: 'Κατηγορία',
                      value: communicationGroupData
                        ? communicationGroupData
                        : 'Δεν έχετε επιλέξει να λαμβάνετε ενημερωτικά newsletters',
                    },
                  ]}
                />
              </Box>
            </Box>
          )}

          {getRole() !== 'admin' ? (
            <Box sx={{ mb: 4 }}>
              <GovButton
                ariaLabel={'Αποθήκευση'}
                type={'submit'}
                variant="contained"
                onClick={() => window.print()}
                background={'#006736'}
                hoverBackground={'#154a2b'}
                borderColor={'#006736'}
                startIcon={<PictureAsPdfIcon />}
              >
                Αποθήκευση
              </GovButton>
            </Box>
          ) : null}
        </Box>
      )}
    </>
  );
};
export default RegistrationPreview;
